// centralised refinery constants – single source of truth for ui layers
// keep alphabetised + documented. if a list should really come from the api,
// comment it clearly so we can swap the source later.

// metals we care about – pulled from melt-record template and existing ui.
export const METAL_SYMBOLS = Object.freeze(['AU', 'AG', 'PT', 'PD'])

export const XRF_CORE_METALS = Object.freeze(['AU', 'AG', 'PT', 'PD', 'CU', 'ZN', 'NI'])

// Optional XRF metals (includes IR/RH/RU for the PGM haircut set).
export const XRF_OPTIONAL_METALS = Object.freeze([
  'CD', 'SN', 'PB', 'IN', 'GE', 'AL', 'MN', 'TI', 'FE', 'IR', 'RH', 'RU',
])

export const XRF_ALL_METALS = Object.freeze([...XRF_CORE_METALS, ...XRF_OPTIONAL_METALS])

// PGM haircut / averaging set — PT/PD plus IR/RH/RU (OS out of scope).
export const PGM_METALS = Object.freeze(['PT', 'PD', 'IR', 'RH', 'RU'])

export function isPgmMetal(metal) {
  return PGM_METALS.includes(String(metal || '').toUpperCase())
}

// allowed weight units. source of truth should be backend enum (todo: verify).
export const WEIGHT_UNITS = Object.freeze(['TOZ', 'g', 'dwt', 'oz'])

// Matches platformgold-api/models/refinery/constants.js METAL_WEIGHT_EPSILON.
export const METAL_WEIGHT_EPSILON = 0.0001


// dropdown yes/no helpers
export const YES_NO = Object.freeze(['Yes', 'No'])
export const YES_NO_NA = Object.freeze(['Yes', 'No', 'N/A']) // retained for backward-compat in other modules

// must match backend DECLARED_WEIGHT_UPDATE_STATUSES in refining-lots/constants.js
export const DECLARED_WEIGHT_UPDATE_STATUSES = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
})

// must match backend CASH_ADVANCE_CAPACITY_BASES in refining-lots/constants.js
export const CASH_ADVANCE_CAPACITY_BASES = Object.freeze({
  PROJECTED: 'projected',
  ASSAY: 'assay',
})

// allowable deviation between max and min XRF tests in percentage points
// business rule: block save when High/Low > 1.5%
export const SPREAD_THRESHOLD = 1.5

// number of duplicate XRF tests per metal
export const NUM_TESTS = 3

// default calculation factors and tolerances for refinery operations
// these are FALLBACK VALUES in the override hierarchy:
//   1. per-lot calcSnapshot override (highest priority)
//   2. global refinery_settings value (medium priority)
//   3. hardcoded default constant (lowest priority / fallback)

// fire assay calculation factors
export const DEFAULT_PGM_HAIRCUT_FACTOR = 1.0
export const DEFAULT_SECONDARY_HAIRCUT_FACTOR = 0.95
export const DEFAULT_MARGIN_OF_ERROR = 0.995

// assay quality control thresholds
export const DEFAULT_ASSAY_SPREAD_THRESHOLD_AU = 0.015  // max spread (pct) between gold cup samples
export const DEFAULT_ASSAY_SPREAD_THRESHOLD_AG = 0.015  // max spread (pct) between silver cup samples
export const DEFAULT_LOSS_TOLERANCE_PCT = 0.2           // allowed melt/assay loss as percent of starting weight

// precision settings for display and rounding
export const DEFAULT_WEIGHT_PRECISION = 4          // decimal places for fine troy ounce values
export const DEFAULT_PURITY_PRECISION = 4            // decimal places for purity values (0-1 scale, e.g. 0.9287 → 92.87%)
export const DEFAULT_CURRENCY_PRECISION = 2          // standard USD display ($1,234.56)
export const DEFAULT_EXTENDED_CURRENCY_PRECISION = 4 // spot prices, precise transactions ($1,234.5678)
export const DEFAULT_PER_METAL_PRECISION = Object.freeze({ AU: 3, AG: 1, PT: 3, PD: 3, IR: 3, RH: 3, RU: 3 }) // per-metal weight precision

// global numeric limits used across refinery inputs
// weights (troy ounces)
export const WEIGHT_OZT_MIN = 0.001
export const WEIGHT_OZT_MAX = 100000

export const SPOT_LOCK_FULFILLMENT_WINDOW_DAYS = 28
export const SPOT_LOCK_FULFILLMENT_NOTICE = `Spot locks must be fulfilled within ${SPOT_LOCK_FULFILLMENT_WINDOW_DAYS} days.`

// percentage inputs (0–100 scale shown to user)
export const PERCENT_MIN = 0.0
export const PERCENT_MAX = 99.99

// purity inputs when represented as decimals (0–1 scale)
export const PURITY_DECIMAL_MIN = 0.0
export const PURITY_DECIMAL_MAX = 0.9999

// fire assay grams inputs (sample cup weights)
// gross sample weight must be within this range
export const FIRE_ASSAY_GRAMS_MIN = 0.5
export const FIRE_ASSAY_GRAMS_MAX = 9.999

// default turnaround (TAT) in days to derive assay due date when contract
// does not specify explicit processing timeframe.
export const DEFAULT_ASSAY_TAT_DAYS = 5

// draft type identifier for lot assay drafts
export const RL_DRAFT = 'RLDRAFT'

// primary metals display names
export const PRIMARY_METALS = Object.freeze({
  GOLD: 'Gold',
  SILVER: 'Silver',
  PLATINUM: 'Platinum',
  PALLADIUM: 'Palladium'
})

// metal symbols mapping from display names to symbols
export const METAL_SYMBOL_MAP = Object.freeze({
  [PRIMARY_METALS.GOLD]: 'AU',
  [PRIMARY_METALS.SILVER]: 'AG', 
  [PRIMARY_METALS.PLATINUM]: 'PT',
  [PRIMARY_METALS.PALLADIUM]: 'PD'
})

export const METAL_SYMBOL = Object.freeze({
  GOLD: 'AU',
  SILVER: 'AG',
  PLATINUM: 'PT',
  PALLADIUM: 'PD'
})

export const METAL_WALLET_QUERY_TABS = Object.freeze({
  UNSETTLED_SPOT_LOCKS: 'unsettled-spot-locks',
  SETTLED_SPOT_LOCKS: 'settled-spot-locks',
})

export const METAL_WALLET_HISTORY_TABS = Object.freeze({
  WALLET_BALANCE: 0,
  UNSETTLED_SPOT_LOCKS: 1,
  SETTLED_SPOT_LOCKS: 2,
})

export const METAL_WALLET_TAB_INDEX_BY_QUERY = Object.freeze({
  [METAL_WALLET_QUERY_TABS.UNSETTLED_SPOT_LOCKS]: METAL_WALLET_HISTORY_TABS.UNSETTLED_SPOT_LOCKS,
  [METAL_WALLET_QUERY_TABS.SETTLED_SPOT_LOCKS]: METAL_WALLET_HISTORY_TABS.SETTLED_SPOT_LOCKS,
})

export const METAL_WALLET_QUERY_TAB_BY_INDEX = Object.freeze({
  [METAL_WALLET_HISTORY_TABS.UNSETTLED_SPOT_LOCKS]: METAL_WALLET_QUERY_TABS.UNSETTLED_SPOT_LOCKS,
  [METAL_WALLET_HISTORY_TABS.SETTLED_SPOT_LOCKS]: METAL_WALLET_QUERY_TABS.SETTLED_SPOT_LOCKS,
})

// metal symbol → customer-facing display name (AU → Gold, etc.)
export const METAL_NAME = Object.freeze({
  AU: PRIMARY_METALS.GOLD,
  AG: PRIMARY_METALS.SILVER,
  PT: PRIMARY_METALS.PLATINUM,
  PD: PRIMARY_METALS.PALLADIUM
})

// resolve a metal symbol to its display name, falling back to the symbol itself
export const metalDisplayName = (symbol) => METAL_NAME[symbol] || symbol

// default metal symbol
export const DEFAULT_METAL_SYMBOL = METAL_SYMBOL.GOLD

// bar number extraction pattern
export const BAR_NUMBER_PATTERN = /-(\d+)$/

/** Lot statuses where settlement-related fields (e.g. net weight) are visible to customers. */
export const SETTLEMENT_VISIBLE_STATUSES = Object.freeze(['awaiting_customer_approval', 'settled'])

// valid metadata fields for SET_METADATA_FIELD mutation
export const VALID_METADATA_FIELDS = Object.freeze([
  'primaryMetal',
  'technician',
  'assayDate',
  'notes',
  'temperature',
  'humidity',
  'equipment'
])

// error messages
export const ERROR_MESSAGES = Object.freeze({
  NO_LOT_ID: 'lotId required to save draft',
  NO_LOT_ASSAY_ID: 'No lot assay ID to finalize',
  DRAFT_CREATION_FAILED: 'failed to create draft prior to finalization',
  INVALID_BAR_NUMBER: 'Invalid bar number format'
})

// map for quick label → enum lookup (e.g., weight unit abbreviation → api enum)
export const WEIGHT_UNIT_MAP = Object.freeze(
  WEIGHT_UNITS.reduce((acc, u) => {
    acc[u] = u
    return acc
  }, {})
)

export default {
  METAL_SYMBOLS,
  XRF_CORE_METALS,
  XRF_OPTIONAL_METALS,
  XRF_ALL_METALS,
  PGM_METALS,
  isPgmMetal,
  WEIGHT_UNITS,
  METAL_WEIGHT_EPSILON,
  DECLARED_WEIGHT_UPDATE_STATUSES,
  CASH_ADVANCE_CAPACITY_BASES,
  YES_NO,
  YES_NO_NA,
  WEIGHT_UNIT_MAP,
  SPREAD_THRESHOLD,
  NUM_TESTS,
  DEFAULT_PGM_HAIRCUT_FACTOR,
  DEFAULT_SECONDARY_HAIRCUT_FACTOR,
  DEFAULT_MARGIN_OF_ERROR,
  RL_DRAFT,
  PRIMARY_METALS,
  METAL_SYMBOL_MAP,
  METAL_SYMBOL,
  METAL_NAME,
  metalDisplayName,
  DEFAULT_METAL_SYMBOL,
  BAR_NUMBER_PATTERN,
  VALID_METADATA_FIELDS,
  ERROR_MESSAGES,
  SETTLEMENT_VISIBLE_STATUSES,
}
